import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Briefcase, Mail, FileText, LogOut, Download, ShieldCheck, TrendingUp, GraduationCap, Award, ExternalLink, Send, User, CheckCircle2, Archive, Trash2, RotateCcw } from "lucide-react";import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import api, { API } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import CandidateEmailModal from "../components/CandidateEmailModal";
import AdminProfileModal from "../components/AdminProfileModal";
import EnrollmentDetailModal from "../components/EnrollmentDetailModal";
import AdminJobs from "../components/AdminJobs";
import AdminApplications from "../components/AdminApplications";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-lux" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-md bg-[#10B981]/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#10B981]" />
        </div>
        <div className="font-mono text-[10px] tracking-[0.3em] text-navy/40">LIVE</div>
      </div>
      <div className="mt-6 font-serif text-4xl text-navy">{value ?? "—"}</div>
      <div className="mt-1 eyebrow text-navy/50">{label}</div>
    </div>
  );
}

function Table({ headers, rows, renderRow, empty = "Nothing yet." }) {
  if (!rows?.length) {
    return <div className="text-center py-16 text-navy/50">{empty}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-navy/10 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-navy text-white">
          <tr>
            {headers.map((h) => (
              <th key={h} className="text-left font-medium px-5 py-3.5 text-xs uppercase tracking-widest">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-navy/5">
          {rows.map((r, idx) => renderRow(r, idx))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active"); // 'active' or 'archived'
  const [emailCandidate, setEmailCandidate] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [reviewEnrollmentId, setReviewEnrollmentId] = useState(null);
  const [pipelineMeta, setPipelineMeta] = useState({ statuses: [], labels: {} });
  const email = localStorage.getItem("dhp_admin_email");

  useEffect(() => {
    const token = localStorage.getItem("dhp_admin_token");
    if (!token) {
      nav("/admin/login");
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        const a = view === "archived" ? "true" : "false";
        const [s, c, e, ct, l, en, v] = await Promise.all([
          api.get("/admin/stats"),
          api.get(`/admin/candidates?archived=${a}`),
          api.get(`/admin/employers?archived=${a}`),
          api.get(`/admin/contacts?archived=${a}`),
          api.get(`/admin/leads?archived=${a}`),
          api.get(`/admin/enrollments?archived=${a}`),
          api.get(`/admin/id-verifications?archived=${a}`),
        ]);
        setStats(s.data);
        setCandidates(c.data);
        setEmployers(e.data);
        setContacts(ct.data);
        setLeads(l.data);
        setEnrollments(en.data);
        setVerifications(v.data);
        // Pipeline metadata (best-effort)
        try {
          const pm = await api.get("/admin/pipeline/statuses");
          setPipelineMeta(pm.data);
        } catch { /* ignore */ }
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem("dhp_admin_token");
          nav("/admin/login");
          return;
        }
        toast.error("Unable to load data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [nav, view]);

  // Helper to reload all data after archive/restore/delete
  const reloadAll = async () => {
    const a = view === "archived" ? "true" : "false";
    const [s, c, e, ct, l, en, v] = await Promise.all([
      api.get("/admin/stats"),
      api.get(`/admin/candidates?archived=${a}`),
      api.get(`/admin/employers?archived=${a}`),
      api.get(`/admin/contacts?archived=${a}`),
      api.get(`/admin/leads?archived=${a}`),
      api.get(`/admin/enrollments?archived=${a}`),
      api.get(`/admin/id-verifications?archived=${a}`),
    ]);
    setStats(s.data);
    setCandidates(c.data);
    setEmployers(e.data);
    setContacts(ct.data);
    setLeads(l.data);
    setEnrollments(en.data);
    setVerifications(v.data);
  };

  const archiveItem = async (collection, id, label) => {
    if (!window.confirm(`Archive ${label}? You can restore it from the Archived view.`)) return;
    try {
      await api.patch(`/admin/${collection}/${id}/archive`);
      toast.success("Moved to archive");
      await reloadAll();
    } catch { toast.error("Could not archive"); }
  };

  const restoreItem = async (collection, id, label) => {
    try {
      await api.patch(`/admin/${collection}/${id}/restore`);
      toast.success(`Restored ${label}`);
      await reloadAll();
    } catch { toast.error("Could not restore"); }
  };

  const deleteItem = async (collection, id, label) => {
    if (!window.confirm(`PERMANENTLY DELETE ${label}? This cannot be undone.`)) return;
    if (!window.confirm("Are you absolutely sure? This is permanent.")) return;
    try {
      await api.delete(`/admin/purge/${collection}/${id}`);
      toast.success("Deleted permanently");
      await reloadAll();
    } catch { toast.error("Could not delete"); }
  };

  const logout = () => {
    localStorage.removeItem("dhp_admin_token");
    localStorage.removeItem("dhp_admin_email");
    nav("/admin/login");
  };

  // Map common content-types → file extension fallback when filename is missing/extension-less
  const extFromContentType = (ct) => {
    if (!ct) return "";
    const map = {
      "application/pdf": ".pdf",
      "application/msword": ".doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/png": ".png",
      "image/heic": ".heic",
      "image/webp": ".webp",
      "text/plain": ".txt",
    };
    return map[ct.toLowerCase()] || "";
  };

  // Build a safe download filename: prefer original upload filename; otherwise
  // derive from candidate name + correct extension based on content-type.
  const safeFilename = (originalFilename, contentType, fallbackBase) => {
    const safeBase = String(fallbackBase || "file").replace(/\s+/g, "_");
    if (originalFilename && /\.[A-Za-z0-9]{1,8}$/.test(originalFilename)) return originalFilename;
    const ext = extFromContentType(contentType);
    return `${safeBase}${ext}`;
  };

  const downloadCv = async (row) => {
    try {
      const token = localStorage.getItem("dhp_admin_token");
      const res = await fetch(`${API}/admin/candidates/${row.id}/cv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const ct = res.headers.get("content-type") || row.cv_content_type || "";
      const blob = await res.blob();
      const typedBlob = blob.type ? blob : new Blob([blob], { type: ct });
      const url = URL.createObjectURL(typedBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = safeFilename(row.cv_filename, ct, `CV_${row.full_name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download CV");
    }
  };

  const downloadId = async (row) => {
    try {
      const token = localStorage.getItem("dhp_admin_token");
      const res = await fetch(`${API}/admin/candidates/${row.id}/id`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const ct = res.headers.get("content-type") || row.id_content_type || "";
      const blob = await res.blob();
      const typedBlob = blob.type ? blob : new Blob([blob], { type: ct });
      const url = URL.createObjectURL(typedBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = safeFilename(row.id_filename, ct, `ID_${row.full_name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download ID");
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

  // Per-row archive / restore / delete buttons. Renders different actions based on current view.
  const RowActions = ({ collection, id, label }) => {
    if (view === "archived") {
      return (
        <div className="inline-flex items-center gap-2.5">
          <button onClick={() => restoreItem(collection, id, label)} className="text-[#10B981] hover:underline inline-flex items-center gap-1 text-xs font-medium" data-testid={`restore-${collection}-${id}`} title="Restore from archive">
            <RotateCcw className="h-3.5 w-3.5" /> Restore
          </button>
          <button onClick={() => deleteItem(collection, id, label)} className="text-red-600 hover:underline inline-flex items-center gap-1 text-xs font-medium" data-testid={`delete-${collection}-${id}`} title="Permanently delete">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2.5">
        <button onClick={() => archiveItem(collection, id, label)} className="text-navy/60 hover:text-navy inline-flex items-center gap-1 text-xs font-medium" data-testid={`archive-${collection}-${id}`} title="Move to archive (recoverable)">
          <Archive className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => deleteItem(collection, id, label)} className="text-red-600/70 hover:text-red-600 inline-flex items-center gap-1 text-xs font-medium" data-testid={`delete-${collection}-${id}`} title="Permanently delete">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  const downloadVerificationId = async (v) => {
    try {
      const token = localStorage.getItem("dhp_admin_token");
      const res = await fetch(`${API}/admin/id-verifications/${v.id}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const ct = res.headers.get("content-type") || v.id_content_type || "";
      const blob = await res.blob();
      const typedBlob = blob.type ? blob : new Blob([blob], { type: ct });
      const url = URL.createObjectURL(typedBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = safeFilename(v.id_filename, ct, `ID_${v.full_name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download verification ID");
    }
  };

  const downloadCertificate = async (r) => {
    try {
      const token = localStorage.getItem("dhp_admin_token");
      const res = await fetch(`${API}/admin/enrollments/${r.id}/certificate.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `DirectHire_PRC_${r.prc_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download certificate");
    }
  };

  const issuePrc = async (enrollmentId, name) => {
    if (!window.confirm(`Issue PRC for ${name}? An email with the verification ID will be sent.`)) return;
    try {
      const res = await api.post(`/admin/enrollments/${enrollmentId}/issue-prc`);
      toast.success(`PRC issued: ${res.data.prc_id}${res.data.already_issued ? " (already issued)" : ""}`);
      const en = await api.get("/admin/enrollments");
      setEnrollments(en.data);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to issue PRC");
    }
  };

  const updatePipeline = async (enrollmentId, newStatus) => {
    try {
      await api.post(`/admin/enrollments/${enrollmentId}/pipeline`, { pipeline_status: newStatus });
      toast.success(`Pipeline → ${pipelineMeta.labels?.[newStatus] || newStatus}`);
      const a = view === "archived" ? "true" : "false";
      const en = await api.get(`/admin/enrollments?archived=${a}`);
      setEnrollments(en.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update pipeline");
    }
  };

  // ---- UTM aggregations for the Source Analytics charts ----
  const sourceData = useMemo(() => {
    const counts = {};
    for (const c of candidates) {
      const key = (c.utm_source || "direct").toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [candidates]);

  const campaignData = useMemo(() => {
    const counts = {};
    for (const c of candidates) {
      if (!c.utm_campaign) continue;
      const key = c.utm_campaign;
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [candidates]);

  const SOURCE_PALETTE = ["#10B981", "#0A2540", "#D4AF37", "#22C55E", "#0EA5E9", "#8B5CF6", "#F59E0B", "#94A3B8"];
  const CAMPAIGN_PALETTE = ["#D4AF37", "#0A2540", "#10B981", "#F59E0B", "#8B5CF6", "#0EA5E9", "#22C55E", "#94A3B8"];

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard-page">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-white/5 flex items-center justify-center ring-1 ring-white/10">
              <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg">Admin Console</div>
              <div className="eyebrow text-[10px] text-white/55">Direct Hire Team</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-white/50 hidden md:block">{email}</div>
            <button onClick={() => setShowProfile(true)} className="btn-ghost !border-white/20 !text-white hover:!bg-white hover:!text-navy !py-2 !px-4 text-sm" data-testid="admin-profile-btn">
              <User className="h-4 w-4" /> Profile
            </button>
            <button onClick={logout} className="btn-ghost !border-white/20 !text-white hover:!bg-white hover:!text-navy !py-2 !px-4 text-sm" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="eyebrow text-[#10B981]">Overview</div>
            <h1 className="mt-2 font-serif text-4xl text-navy">Dashboard</h1>
          </div>
          {/* Active / Archived toggle */}
          <div className="inline-flex rounded-md border border-navy/15 bg-white p-1" data-testid="view-toggle">
            <button
              onClick={() => setView("active")}
              className={`px-4 py-2 text-sm font-medium rounded transition-all ${view === "active" ? "bg-[#10B981] text-white" : "text-navy/65 hover:text-navy"}`}
              data-testid="view-active"
            >
              Active
            </button>
            <button
              onClick={() => setView("archived")}
              className={`px-4 py-2 text-sm font-medium rounded transition-all inline-flex items-center gap-1.5 ${view === "archived" ? "bg-navy text-white" : "text-navy/65 hover:text-navy"}`}
              data-testid="view-archived"
            >
              <Archive className="h-3.5 w-3.5" /> Archived {stats?.archived_total ? `(${stats.archived_total})` : ""}
            </button>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-6 gap-4">
          <Stat icon={Users} label="Candidates" value={stats?.candidates} />
          <Stat icon={GraduationCap} label="Enrollments" value={stats?.enrollments} />
          <Stat icon={ShieldCheck} label="ID Verifications" value={stats?.id_verifications} />
          <Stat icon={Briefcase} label="Employer Quotes" value={stats?.employer_quotes} />
          <Stat icon={Mail} label="Contacts" value={stats?.contacts} />
          <Stat icon={FileText} label="Lead Magnets" value={stats?.leads} />
        </div>

        <div className="mt-14">
          <Tabs defaultValue="applications" className="w-full">
            <TabsList data-testid="admin-tabs">
              <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
              <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
              <TabsTrigger value="candidates" data-testid="tab-candidates">Candidates</TabsTrigger>
              <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
              <TabsTrigger value="verifications" data-testid="tab-verifications">ID Verifications</TabsTrigger>
              <TabsTrigger value="employers" data-testid="tab-employers">Employer Quotes</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
              <TabsTrigger value="leads" data-testid="tab-leads">Lead Magnets</TabsTrigger>
            </TabsList>

            <TabsContent value="applications" className="mt-6">
              <AdminApplications />
            </TabsContent>

            <TabsContent value="jobs" className="mt-6">
              <AdminJobs />
            </TabsContent>

            <TabsContent value="candidates" className="mt-6">
              {/* SOURCE ANALYTICS — Two side-by-side charts */}
              <div className="grid md:grid-cols-2 gap-5 mb-6" data-testid="source-analytics">
                <div className="card-lux !p-6" data-testid="chart-by-source">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="eyebrow text-[#10B981] flex items-center gap-2">
                        <TrendingUp className="h-3.5 w-3.5" /> By Source
                      </div>
                      <div className="mt-1 font-serif text-xl text-navy">Where applicants come from</div>
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-navy/40">
                      {candidates.length} TOTAL
                    </div>
                  </div>
                  {sourceData.length === 0 ? (
                    <div className="h-[220px] flex items-center justify-center text-sm text-navy/40">
                      No candidate data yet.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={sourceData} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} width={28} />
                        <Tooltip
                          cursor={{ fill: "#0A2540", fillOpacity: 0.04 }}
                          contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                          formatter={(v) => [`${v} applicant${v === 1 ? "" : "s"}`, "Count"]}
                        />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                          {sourceData.map((_, i) => (
                            <Cell key={i} fill={SOURCE_PALETTE[i % SOURCE_PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="card-lux !p-6" data-testid="chart-by-campaign">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="eyebrow text-[#D4AF37] flex items-center gap-2">
                        <TrendingUp className="h-3.5 w-3.5" /> By Campaign
                      </div>
                      <div className="mt-1 font-serif text-xl text-navy">Which campaigns convert</div>
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-navy/40">
                      {campaignData.length} ACTIVE
                    </div>
                  </div>
                  {campaignData.length === 0 ? (
                    <div className="h-[220px] flex items-center justify-center text-sm text-navy/40 text-center px-4">
                      No tagged campaigns yet. Add <span className="font-mono text-navy/60 mx-1">?utm_campaign=…</span> to your links.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={campaignData} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} interval={0} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} width={28} />
                        <Tooltip
                          cursor={{ fill: "#D4AF37", fillOpacity: 0.06 }}
                          contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                          formatter={(v) => [`${v} applicant${v === 1 ? "" : "s"}`, "Count"]}
                        />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                          {campaignData.map((_, i) => (
                            <Cell key={i} fill={CAMPAIGN_PALETTE[i % CAMPAIGN_PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <Table
                headers={["Date", "Name", "Email", "State", "Source", "Contact", "CV", "ID", "Email", "Actions"]}
                rows={candidates}
                empty={loading ? "Loading…" : "No candidate applications yet."}
                renderRow={(r) => {
                  const daysSinceContact = r.last_contacted_at
                    ? Math.floor((Date.now() - new Date(r.last_contacted_at).getTime()) / 86400000)
                    : null;
                  return (
                  <tr key={r.id} className="hover:bg-navy/[0.02]">
                    <td className="px-5 py-3 text-navy/60 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-5 py-3 font-medium text-navy">{r.full_name}</td>
                    <td className="px-5 py-3 text-navy/70">{r.email}</td>
                    <td className="px-5 py-3 text-navy/70">{r.preferred_state || "—"}</td>
                    <td className="px-5 py-3">
                      {r.utm_source
                        ? <span className="px-2 py-1 rounded bg-[#10B981]/10 text-[#10B981] text-xs font-medium" title={`${r.utm_medium || ""} · ${r.utm_campaign || ""}`}>{r.utm_source}{r.utm_campaign ? ` · ${r.utm_campaign}` : ""}</span>
                        : <span className="text-navy/40 text-xs">direct</span>}
                    </td>
                    <td className="px-5 py-3 text-xs">
                      {r.email_count > 0
                        ? <span className={`inline-flex items-center gap-1 px-2 py-1 rounded font-medium ${daysSinceContact >= 5 ? "bg-amber-100 text-amber-800" : "bg-[#10B981]/10 text-[#10B981]"}`} title={`${r.email_count} email${r.email_count === 1 ? "" : "s"} sent`}>
                            {daysSinceContact === 0 ? "Today" : daysSinceContact === 1 ? "1d ago" : `${daysSinceContact}d ago`}
                          </span>
                        : <span className="text-navy/40">Never</span>}
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => downloadCv(r)} className="text-[#10B981] font-medium hover:underline inline-flex items-center gap-1.5 text-sm" data-testid={`cv-dl-${r.id}`}>
                        <Download className="h-3.5 w-3.5" /> CV
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      {r.id_filename
                        ? (
                          <button onClick={() => downloadId(r)} className="text-[#D4AF37] font-medium hover:underline inline-flex items-center gap-1.5 text-sm" data-testid={`id-dl-${r.id}`}>
                            <Download className="h-3.5 w-3.5" /> ID
                          </button>
                        )
                        : <span className="text-navy/40 text-xs">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => setEmailCandidate(r)} className="text-navy font-medium hover:text-[#10B981] inline-flex items-center gap-1.5 text-sm transition-colors" data-testid={`email-btn-${r.id}`}>
                        <Send className="h-3.5 w-3.5" /> Email
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <RowActions collection="candidates" id={r.id} label={r.full_name} />
                    </td>
                  </tr>
                  );
                }}
              />
            </TabsContent>

            <TabsContent value="enrollments" className="mt-6">
              <Table
                headers={["Date", "Name", "Email", "Track", "Pipeline", "Progress", "Status", "Portal", "PRC", "Actions"]}
                rows={enrollments}
                empty={loading ? "Loading…" : "No enrollments yet. Share /enroll with candidates to get started."}
                renderRow={(r) => (
                  <tr key={r.id} className="hover:bg-navy/[0.02]">
                    <td className="px-5 py-3 text-navy/60 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-5 py-3 font-medium text-navy">{r.full_name}</td>
                    <td className="px-5 py-3 text-navy/70">{r.email}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-1 rounded bg-[#10B981]/10 text-[#10B981] text-xs font-medium">{r.track}</span>
                    </td>
                    <td className="px-5 py-3">
                      <select
                        value={r.pipeline_status || "applied"}
                        onChange={(e) => updatePipeline(r.id, e.target.value)}
                        className="text-xs rounded-md border border-navy/15 bg-white px-2 py-1 font-medium text-navy focus:ring-2 focus:ring-[#10B981] focus:outline-none"
                        data-testid={`pipeline-select-${r.id}`}
                      >
                        {(pipelineMeta.statuses || []).map((s) => (
                          <option key={s} value={s}>{pipelineMeta.labels?.[s] || s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3 text-navy/70">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-navy/10 overflow-hidden">
                          <div className="h-full bg-[#10B981]" style={{ width: `${Math.min(100, (r.days_completed / r.total_days) * 100)}%` }} />
                        </div>
                        <span className="text-xs font-mono">{r.days_completed}/{r.total_days}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {r.status === "completed"
                        ? <span className="px-2 py-1 rounded bg-[#D4AF37]/15 text-[#8A6D14] text-xs font-medium inline-flex items-center gap-1"><Award className="h-3 w-3" /> Completed</span>
                        : <span className="px-2 py-1 rounded bg-navy/5 text-navy/70 text-xs font-medium">Active · Day {r.current_day}</span>}
                    </td>
                    <td className="px-5 py-3">
                      <a href={r.portal_url} target="_blank" rel="noopener noreferrer" className="text-[#10B981] font-medium hover:underline inline-flex items-center gap-1.5 text-sm" data-testid={`portal-link-${r.id}`}>
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                      </a>
                    </td>
                    <td className="px-5 py-3">
                      {r.prc_id
                        ? (
                          <div className="inline-flex items-center gap-2">
                            <span className="font-mono text-xs text-[#8A6D14]">{r.prc_id}</span>
                            <button
                              onClick={() => downloadCertificate(r)}
                              className="text-[#10B981] hover:underline inline-flex items-center gap-1 text-xs font-medium"
                              data-testid={`cert-dl-${r.id}`}
                              title="Download certificate PDF"
                            >
                              <Download className="h-3 w-3" /> PDF
                            </button>
                          </div>
                        )
                        : r.days_completed >= r.total_days
                          ? <button onClick={() => issuePrc(r.id, r.full_name)} className="text-[#D4AF37] font-medium hover:underline inline-flex items-center gap-1.5 text-sm" data-testid={`issue-prc-${r.id}`}>
                              <Award className="h-3.5 w-3.5" /> Issue PRC
                            </button>
                          : <span className="text-navy/40 text-xs">In progress</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setReviewEnrollmentId(r.id)}
                          className="text-[#10B981] font-medium hover:underline text-xs inline-flex items-center gap-1"
                          data-testid={`review-enrollment-${r.id}`}
                          title="Review answers and set decision letter"
                        >
                          Review
                        </button>
                        <RowActions collection="enrollments" id={r.id} label={r.full_name} />
                      </div>
                    </td>
                  </tr>
                )}
              />
            </TabsContent>

            <TabsContent value="verifications" className="mt-6">
              <Table
                headers={["Date", "Name", "Email", "DOB", "Phone", "Address", "ID Type", "Matched", "File", "Actions"]}
                rows={verifications}
                empty={loading ? "Loading…" : "No ID verifications yet. Candidates submit at /verify-id."}
                renderRow={(r) => (
                  <tr key={r.id} className="hover:bg-navy/[0.02]">
                    <td className="px-5 py-3 text-navy/60 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-5 py-3 font-medium text-navy">{r.full_name}</td>
                    <td className="px-5 py-3 text-navy/70">{r.email}</td>
                    <td className="px-5 py-3 text-navy/70 whitespace-nowrap">{r.date_of_birth || "—"}</td>
                    <td className="px-5 py-3 text-navy/70 whitespace-nowrap">{r.phone || "—"}</td>
                    <td className="px-5 py-3 text-navy/70 max-w-xs truncate" title={r.home_address}>{r.home_address}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-1 rounded bg-[#10B981]/10 text-[#10B981] text-xs font-medium">{r.id_type}</span>
                    </td>
                    <td className="px-5 py-3">
                      {r.matched_candidate_id
                        ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#10B981]/10 text-[#10B981] text-xs font-medium" title="Auto-matched to CV on file"><CheckCircle2 className="h-3 w-3" /> Linked</span>
                        : <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 text-xs font-medium">No CV match</span>}
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => downloadVerificationId(r)} className="text-[#D4AF37] font-medium hover:underline inline-flex items-center gap-1.5 text-sm" data-testid={`verif-dl-${r.id}`}>
                        <Download className="h-3.5 w-3.5" /> ID
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <RowActions collection="id-verifications" id={r.id} label={r.full_name} />
                    </td>
                  </tr>
                )}
              />
            </TabsContent>

            <TabsContent value="employers" className="mt-6">
              <Table
                headers={["Date", "Company", "Contact", "Email", "Roles", "Urgency", "Actions"]}
                rows={employers}
                empty={loading ? "Loading…" : "No employer quotes yet."}
                renderRow={(r) => (
                  <tr key={r.id} className="hover:bg-navy/[0.02]">
                    <td className="px-5 py-3 text-navy/60 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-5 py-3 font-medium text-navy">{r.company_name}</td>
                    <td className="px-5 py-3 text-navy/70">{r.full_name}</td>
                    <td className="px-5 py-3 text-navy/70">{r.email}</td>
                    <td className="px-5 py-3 text-navy/70">{r.roles_needed}</td>
                    <td className="px-5 py-3"><span className="px-2 py-1 rounded bg-[#10B981]/10 text-[#10B981] text-xs font-medium">{r.urgency}</span></td>
                    <td className="px-5 py-3"><RowActions collection="employer-quotes" id={r.id} label={r.company_name} /></td>
                  </tr>
                )}
              />
            </TabsContent>

            <TabsContent value="contacts" className="mt-6">
              <Table
                headers={["Date", "Name", "Email", "Company", "Urgency", "Message", "Actions"]}
                rows={contacts}
                empty={loading ? "Loading…" : "No contact submissions yet."}
                renderRow={(r) => (
                  <tr key={r.id} className="hover:bg-navy/[0.02]">
                    <td className="px-5 py-3 text-navy/60 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-5 py-3 font-medium text-navy">{r.full_name}</td>
                    <td className="px-5 py-3 text-navy/70">{r.email}</td>
                    <td className="px-5 py-3 text-navy/70">{r.company_name || "—"}</td>
                    <td className="px-5 py-3 text-navy/70">{r.urgency}</td>
                    <td className="px-5 py-3 text-navy/70 max-w-sm truncate">{r.message}</td>
                    <td className="px-5 py-3"><RowActions collection="contacts" id={r.id} label={r.full_name} /></td>
                  </tr>
                )}
              />
            </TabsContent>

            <TabsContent value="leads" className="mt-6">
              <Table
                headers={["Date", "Name", "Email", "Company", "Magnet", "Actions"]}
                rows={leads}
                empty={loading ? "Loading…" : "No lead magnet captures yet."}
                renderRow={(r) => (
                  <tr key={r.id} className="hover:bg-navy/[0.02]">
                    <td className="px-5 py-3 text-navy/60 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-5 py-3 font-medium text-navy">{r.full_name}</td>
                    <td className="px-5 py-3 text-navy/70">{r.email}</td>
                    <td className="px-5 py-3 text-navy/70">{r.company || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${r.magnet === "salary_report" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#D4AF37]/15 text-[#8A6D14]"}`}>
                        {r.magnet === "salary_report" ? "Salary Report" : "Interview Guide"}
                      </span>
                    </td>
                    <td className="px-5 py-3"><RowActions collection="leads" id={r.id} label={r.full_name} /></td>
                  </tr>
                )}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {emailCandidate && (
        <CandidateEmailModal
          candidate={emailCandidate}
          onClose={() => {
            setEmailCandidate(null);
            // Refresh candidates so the "last contacted" badge updates
            api.get("/admin/candidates").then((r) => setCandidates(r.data)).catch(() => {});
          }}
        />
      )}

      {showProfile && <AdminProfileModal onClose={() => setShowProfile(false)} />}

      {reviewEnrollmentId && (
        <EnrollmentDetailModal
          enrollmentId={reviewEnrollmentId}
          onClose={() => setReviewEnrollmentId(null)}
          onChanged={async () => {
            const a = view === "archived" ? "true" : "false";
            const en = await api.get(`/admin/enrollments?archived=${a}`);
            setEnrollments(en.data);
          }}
        />
      )}
    </div>
  );
}
